/**
 * Device Token Bridge — WKWebView ↔ Web APNs Token Lifecycle
 * Contract Group® — Quantis Platform
 *
 * Provides a fully web-driven APNs device-token registration system.
 * After login, the web side obtains and registers the device token
 * automatically — no native-side registration logic required.
 *
 * ── Token acquisition strategy (priority order) ─────────────────────────────
 *
 * 1. GLOBAL VARIABLE (preferred — simplest native integration):
 *    The native shell injects the APNs device token into the WKWebView's
 *    JavaScript context as soon as it receives it from iOS:
 *
 *      webView.evaluateJavaScript("""
 *        window.__QUANTIS_DEVICE_TOKEN__    = '\(hexToken)';
 *        window.__QUANTIS_DEVICE_PLATFORM__ = 'ios';
 *      """)
 *
 *    The web reads this global on every login — zero message handlers needed.
 *
 * 2. SESSION STORAGE CACHE:
 *    If a token was obtained during a previous login in this browser
 *    session, it's reused without a native round-trip (APNs tokens don't
 *    change between cold launches).
 *
 * 3. MESSAGE HANDLER REQUEST (legacy fallback):
 *    webkit.messageHandlers.deviceToken.postMessage('getToken')
 *    Native responds via window.quantisDeviceTokenResult(token, platform).
 *
 * 4. PROACTIVE PUSH (token refresh at any time):
 *    Native can call window.quantisSetDeviceToken(token, platform).
 *    If a user is logged in, the new token is registered immediately.
 *
 * ── Registration endpoint ───────────────────────────────────────────────────
 *
 * When the user's Supabase JWT is available (always after login), the web
 * uses the JWT-hardened POST /device-register endpoint which:
 *   - Validates the Bearer token server-side
 *   - Derives user_id from the JWT (never trusts the body)
 *   - Accepts { device_token, platform } in snake_case
 *
 * This matches the standalone edge function exactly.
 *
 * ── Integration ─────────────────────────────────────────────────────────────
 *
 * Called from AuthContext.login() — no manual user action required.
 * The native shell's ONLY job is injecting the global variable (strategy 1)
 * or implementing the message handler (strategy 3). All registration,
 * JWT auth, and session management is handled on the web side.
 * ──────────────────────────────────────────────────────────────────────────
 */

import { ApplePushService } from './ApplePushService';
import { supabase } from '../utils/supabase/client';
import { publicAnonKey } from '../utils/supabase/info';

// ── Storage keys ────────────────────────────────────────────────────────────
const CACHE_KEY_TOKEN    = 'quantis_device_token';
const CACHE_KEY_PLATFORM = 'quantis_device_platform';
const CACHE_KEY_APNS_ENV = 'quantis_device_apns_environment';

// ── Global variable names (injected by native shell) ────────────────────────
const GLOBAL_TOKEN_KEY       = '__QUANTIS_DEVICE_TOKEN__';
const GLOBAL_PLATFORM_KEY    = '__QUANTIS_DEVICE_PLATFORM__';
const GLOBAL_APNS_ENV_KEY    = '__QUANTIS_APNS_ENV__';
const GLOBAL_DEVICE_NAME_KEY = '__QUANTIS_DEVICE_NAME__';
const GLOBAL_DEVICE_MODEL_KEY= '__QUANTIS_DEVICE_MODEL__';
const GLOBAL_DEVICE_OS_KEY   = '__QUANTIS_DEVICE_OS__';

// ── Types ───────────────────────────────────────────────────────────────────

type Platform = 'ios' | 'macos';
type ApnsEnvironment = 'development' | 'production';

interface PendingRegistration {
  userId: string;
  userName: string;
  resolve: (registered: boolean) => void;
  timeout: ReturnType<typeof setTimeout>;
}

// ── Extend Window for all bridge interfaces ─────────────────────────────────

declare global {
  interface Window {
    /** Strategy 1: native-injected global device token */
    __QUANTIS_DEVICE_TOKEN__?: string;
    /** Strategy 1: native-injected global platform */
    __QUANTIS_DEVICE_PLATFORM__?: Platform;
    /** Strategy 1: native-injected APNs environment */
    __QUANTIS_APNS_ENV__?: ApnsEnvironment;
    /** Optional: human-readable device name (e.g. "Gary's MacBook Pro") */
    __QUANTIS_DEVICE_NAME__?: string;
    /** Optional: device model identifier (e.g. "Mac16,1") */
    __QUANTIS_DEVICE_MODEL__?: string;
    /** Optional: OS version string (e.g. "15.2") */
    __QUANTIS_DEVICE_OS__?: string;

    /** Strategy 3: response to a getToken request */
    quantisDeviceTokenResult?: (token: string, platform: Platform, apnsEnvironment?: ApnsEnvironment) => void;
    /** Strategy 4: proactive token refresh */
    quantisSetDeviceToken?: (token: string, platform: Platform, apnsEnvironment?: ApnsEnvironment) => void;

    /**
     * Native push tap deep link handler.
     * Called by the native app when the user taps an APNs notification.
     * The native app parses the push payload's `userInfo` and calls:
     *   window.__QUANTIS_OPEN_CHAT__('chatId')
     * The web app navigates to the messages view and opens the chat.
     */
    __QUANTIS_OPEN_CHAT__?: (chatId: string, messageId?: string | null) => void;

    /**
     * Generic push tap deep link handler.
     * The native app calls this with the full userInfo payload from the
     * push notification so the web can route to the correct view.
     */
    __QUANTIS_HANDLE_PUSH__?: (payload: Record<string, any>) => void;

    /**
     * Soft data refresh handler.
     * Called by the native app (background tasks, silent push) instead of
     * wv.reload() so the React app re-syncs data without a full page restart.
     */
    __QUANTIS_REQUEST_REFRESH__?: () => void;

    webkit?: {
      messageHandlers?: {
        deviceToken?: { postMessage: (msg: string) => void };
        biometrics?: { postMessage: (msg: string) => void };
        /** Web -> Native: request native Quick Look preview of a URL */
        openFile?: { postMessage: (msg: string) => void };
        /** Web -> Native: save file data via native save flow */
        saveFile?: { postMessage: (msg: string) => void };
        /** Web -> Native: share a URL via native share sheet (legacy) */
        shareFile?: { postMessage: (msg: string) => void };
        /**
         * Web -> Native: update the app icon badge count.
         * Native handles this with UNUserNotificationCenter.setBadgeCount().
         * postMessage receives the count as a string (e.g. "3" or "0").
         */
        updateBadge?: { postMessage: (count: string) => void };
      };
    };
  }
}

// ── Bridge class ────────────────────────────────────────────────────────────

class DeviceTokenBridgeClass {
  private initialized = false;
  private pending: PendingRegistration | null = null;
  /** The user currently logged in — set by registerOnLogin, cleared by clearUser */
  private currentUserId: string | null = null;
  private currentUserName: string | null = null;

  // ── Public API ──────────────────────────────────────────────────────────

  /**
   * Returns true if the web app is running inside a WKWebView that
   * exposes the deviceToken message handler OR has an injected global token.
   */
  get isNativeShell(): boolean {
    return !!(
      window.webkit?.messageHandlers?.deviceToken ||
      window[GLOBAL_TOKEN_KEY]
    );
  }

  /**
   * Returns the device token from the native-injected global variable,
   * or null if it hasn't been set.
   */
  get injectedToken(): string | null {
    const token = window[GLOBAL_TOKEN_KEY];
    return token && typeof token === 'string' && token.length > 0 ? token : null;
  }

  get injectedPlatform(): Platform {
    const p = window[GLOBAL_PLATFORM_KEY];
    return p === 'macos' ? 'macos' : 'ios';
  }

  get injectedApnsEnvironment(): ApnsEnvironment {
    const environment = window[GLOBAL_APNS_ENV_KEY];
    return environment === 'development' ? 'development' : 'production';
  }

  get injectedDeviceName(): string | undefined {
    const v = window[GLOBAL_DEVICE_NAME_KEY];
    return v && typeof v === 'string' && v.length > 0 ? v : undefined;
  }

  get injectedDeviceModel(): string | undefined {
    const v = window[GLOBAL_DEVICE_MODEL_KEY];
    return v && typeof v === 'string' && v.length > 0 ? v : undefined;
  }

  get injectedOsVersion(): string | undefined {
    const v = window[GLOBAL_DEVICE_OS_KEY];
    return v && typeof v === 'string' && v.length > 0 ? v : undefined;
  }

  /**
   * Returns the cached device token (if any) from a previous registration
   * in this browser session.
   */
  get cachedToken(): string | null {
    try { return sessionStorage.getItem(CACHE_KEY_TOKEN); }
    catch { return null; }
  }

  get cachedPlatform(): Platform {
    try { return (sessionStorage.getItem(CACHE_KEY_PLATFORM) as Platform) || 'ios'; }
    catch { return 'ios'; }
  }

  get cachedApnsEnvironment(): ApnsEnvironment {
    try {
      return sessionStorage.getItem(CACHE_KEY_APNS_ENV) === 'development'
        ? 'development'
        : 'production';
    } catch {
      return 'production';
    }
  }

  /**
   * Initialize the bridge — sets up the global callbacks that the native
   * layer will invoke. Safe to call multiple times (idempotent).
   */
  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    // ── Strategy 3 callback: response to an explicit getToken request ──
    window.quantisDeviceTokenResult = (token: string, platform: Platform, apnsEnvironment?: ApnsEnvironment) => {
      const resolvedEnvironment = apnsEnvironment === 'development' ? 'development' : this.injectedApnsEnvironment;
      this.cacheToken(token, platform, resolvedEnvironment);
      this.fulfillPending(token, platform, resolvedEnvironment);
    };

    // ── Strategy 4 callback: proactive token refresh from native ───────
    window.quantisSetDeviceToken = (token: string, platform: Platform, apnsEnvironment?: ApnsEnvironment) => {
      const resolvedEnvironment = apnsEnvironment === 'development' ? 'development' : this.injectedApnsEnvironment;
      this.cacheToken(token, platform, resolvedEnvironment);

      // If a user is currently logged in, register the new token immediately.
      // Include any device metadata globals that may be set by the shell.
      if (this.currentUserId) {
        this.registerTokenWithBackend(
          this.currentUserId,
          this.currentUserName || 'Unknown',
          token,
          platform,
          resolvedEnvironment,
          this.injectedDeviceName,
          this.injectedDeviceModel,
          this.injectedOsVersion,
        );
      }
    };

    // ── If the native shell already injected a global token, cache it ──
    const injected = this.injectedToken;
    if (injected) {
      this.cacheToken(injected, this.injectedPlatform, this.injectedApnsEnvironment);
    }

    if (this.isNativeShell) {
      console.log('[DeviceTokenBridge] Initialized — WKWebView detected');
    }
  }

  /**
   * Called from AuthContext.login() after successful authentication.
   * Attempts to obtain and register the device token.
   *
   * Token acquisition priority:
   *   1. Native-injected global variable (window.__QUANTIS_DEVICE_TOKEN__)
   *   2. sessionStorage cache from a previous registration
   *   3. Message handler request (webkit.messageHandlers.deviceToken)
   *
   * Registration uses the JWT-hardened /device-register endpoint which
   * derives user_id from the Supabase access token server-side.
   *
   * Returns a promise that resolves to true if registration succeeded,
   * false if not in a native shell or token unavailable.
   */
  async registerOnLogin(userId: string, userName: string): Promise<boolean> {
    this.init();
    this.currentUserId   = userId;
    this.currentUserName = userName;

    // ── Strategy 1: Native-injected global variable (instant, preferred) ─
    const injected = this.injectedToken;
    if (injected) {
      console.log('[DeviceTokenBridge] Strategy 1 — using native-injected global token');
      this.cacheToken(injected, this.injectedPlatform, this.injectedApnsEnvironment);
      return this.registerTokenWithBackend(
        userId,
        userName,
        injected,
        this.injectedPlatform,
        this.injectedApnsEnvironment,
        this.injectedDeviceName,
        this.injectedDeviceModel,
        this.injectedOsVersion,
      );
    }

    // ── Strategy 2: Cached token from this browser session ──────────────
    const cached = this.cachedToken;
    if (cached) {
      console.log('[DeviceTokenBridge] Strategy 2 — using sessionStorage cached token');
      return this.registerTokenWithBackend(userId, userName, cached, this.cachedPlatform, this.cachedApnsEnvironment);
    }

    // ── Strategy 3: Ask the WKWebView via message handler ───────────────
    if (!window.webkit?.messageHandlers?.deviceToken) {
      return false;
    }

    return new Promise<boolean>((resolve) => {
      // Set a timeout — if native doesn't respond in 8s, give up silently
      const timeout = setTimeout(() => {
        console.warn('[DeviceTokenBridge] Native did not respond with token within 8s');
        this.pending = null;
        resolve(false);
      }, 8000);

      this.pending = { userId, userName, resolve, timeout };

      try {
        console.log('[DeviceTokenBridge] Strategy 3 — requesting token from native via message handler...');
        window.webkit!.messageHandlers!.deviceToken!.postMessage('getToken');
      } catch (err) {
        console.error('[DeviceTokenBridge] Failed to send getToken message:', err);
        clearTimeout(timeout);
        this.pending = null;
        resolve(false);
      }
    });
  }

  /**
   * Called from AuthContext.logout() — clears the current user association
   * so proactive token refreshes don't register to a logged-out user.
   */
  clearUser(): void {
    this.currentUserId   = null;
    this.currentUserName = null;
    this.pending         = null;
  }

  /**
   * Deprecated no-op.
   *
   * Native auth state is no longer pushed from the web layer.
   * Platform sign-in should rely on native credential handling and
   * standard Password AutoFill support instead.
   */
  async pushSessionToNative(): Promise<void> {
    return;
  }

  /**
   * Tear down global callbacks (e.g. on unmount).
   */
  destroy(): void {
    delete window.quantisDeviceTokenResult;
    delete window.quantisSetDeviceToken;
    this.initialized     = false;
    this.currentUserId   = null;
    this.currentUserName = null;
    this.pending         = null;
  }

  // ── Internal helpers ────────────────────────────────────────────────────

  private cacheToken(token: string, platform: Platform, apnsEnvironment: ApnsEnvironment): void {
    try {
      sessionStorage.setItem(CACHE_KEY_TOKEN, token);
      sessionStorage.setItem(CACHE_KEY_PLATFORM, platform);
      sessionStorage.setItem(CACHE_KEY_APNS_ENV, apnsEnvironment);
    } catch {
      // sessionStorage might be unavailable in some contexts
    }
  }

  private fulfillPending(token: string, platform: Platform, apnsEnvironment: ApnsEnvironment): void {
    if (!this.pending) return;

    const { userId, userName, resolve, timeout } = this.pending;
    clearTimeout(timeout);
    this.pending = null;

    this.registerTokenWithBackend(userId, userName, token, platform, apnsEnvironment).then(resolve);
  }

  /**
   * Register the given token with the backend — called after token acquisition.
   * Prefer JWT-hardened /device-register endpoint where the user_id is derived
   * server-side from the access token.
   */
  private async registerTokenWithBackend(
    userId: string,
    userName: string,
    token: string,
    platform: 'ios' | 'macos',
    apnsEnvironment: ApnsEnvironment,
    deviceName?: string,
    deviceModel?: string,
    osVersion?: string,
  ): Promise<boolean> {
    try {
      console.log(`[DeviceTokenBridge] Registering ${platform} ${apnsEnvironment} token for user ${userId}...`);

      // ── Get the user's Supabase JWT for the JWT-hardened endpoint ────
      // The /device-register endpoint derives user_id from the Bearer
      // token — never trusts the body. This matches the standalone
      // edge function (device-register.ts) exactly.
      let accessToken: string | undefined;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        accessToken = session?.access_token ?? undefined;
      } catch {
        console.warn('[DeviceTokenBridge] Could not get Supabase session for JWT');
      }

      // ── Guard: Don't attempt registration without a valid access token ──
      if (!accessToken || accessToken === publicAnonKey) {
        console.warn('[DeviceTokenBridge] No valid access token available — skipping device registration');
        return false;
      }

      const result = await ApplePushService.registerDevice(userId, token, platform, apnsEnvironment, accessToken, { deviceName, deviceModel, osVersion });

      if (result.success) {
        console.log(`[DeviceTokenBridge] Device registered: ${result.device?.id}`);
        return true;
      } else {
        console.warn('[DeviceTokenBridge] Registration failed:', result.error);
        return false;
      }
    } catch (err) {
      console.error('[DeviceTokenBridge] Registration error:', err);
      return false;
    }
  }
}

export const DeviceTokenBridge = new DeviceTokenBridgeClass();
